import { useState, useEffect, useLayoutEffect, useRef, type Dispatch, type SetStateAction } from 'react';
import { getAuthRuntimeSessionIdentity } from '@/features/auth/services/authRuntime';
import { getAdminViewAsUserId } from '@/features/auth/services/adminViewAs';
import { ProjectNode, ChecklistItem } from '@/shared/types/projectData';
import { getNodeId, getProjectIdFromNodeId } from '@/features/projects/utils/projectUtils';
import { initializeNewTask } from './TaskDetailsPanelHelpers';
import { panelHasIntent, panelChecklistPending, type PanelPersistenceState } from './TaskDetailsPanelPersistence';
import { getNodeChecklist, withCanonicalChecklist } from './TaskDetailsPanelChecklist';

/* ============================================================================
 * CUSTOM HOOKS FOR TASK DETAILS PANEL
 * ============================================================================ */

/**
 * Hook for managing task state
 */
export const useTaskState = (task: Partial<ProjectNode>, owner?: PanelPersistenceState) => {
  const [editedTask, setEditedTask] = useState<Partial<ProjectNode>>(() => withCanonicalChecklist({ ...initializeNewTask(task), ...owner?.fields, ...owner?.activeFields, ...(owner && panelChecklistPending(owner) ? { checklist: owner.checklist!.desired } : {}) }));
  const canonicalRef = useRef(editedTask);
  const [hasChanges, setHasChanges] = useState(owner ? panelHasIntent(owner) : false);
  const [currentChecklist, setCurrentChecklist] = useState<ChecklistItem[]>(getNodeChecklist(editedTask));
  const [lastLocalUpdate, setLastLocalUpdate] = useState<number>(0);
  
  useEffect(() => {
    const initialTask = withCanonicalChecklist(initializeNewTask(task));
    const previous = canonicalRef.current;
    canonicalRef.current = initialTask;
    if (previous.id !== initialTask.id || previous.projectId !== initialTask.projectId) {
      setEditedTask(initialTask);
      setCurrentChecklist(getNodeChecklist(initialTask));
      setHasChanges(false);
      setLastLocalUpdate(0);
      return;
    }
    const equal = (left: unknown, right: unknown) => JSON.stringify(left) === JSON.stringify(right);
    setEditedTask(draft => {
      const retained = Object.fromEntries(Object.keys(draft)
        .filter(field => owner ? field in owner.fieldVersions || field in owner.activeVersions || (field === 'checklist' && panelChecklistPending(owner)) : !equal(draft[field as keyof ProjectNode], previous[field as keyof ProjectNode]))
        .map(field => [field, draft[field as keyof ProjectNode]]));
      return { ...initialTask, ...retained };
    });
    setCurrentChecklist(draft => owner ? (panelChecklistPending(owner) ? owner.checklist!.desired : getNodeChecklist(initialTask)) : equal(draft, getNodeChecklist(previous)) ? getNodeChecklist(initialTask) : draft);
  }, [task, owner]);
  
  return {
    editedTask,
    setEditedTask,
    hasChanges,
    setHasChanges,
    currentChecklist,
    setCurrentChecklist,
    lastLocalUpdate,
    setLastLocalUpdate
  };
};

/**
 * Hook for managing modal animation state
 */
export const useModalAnimation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [modalHeight, setModalHeight] = useState<number | 'auto'>('auto');
  const [modalWidth, setModalWidth] = useState<number | 'auto'>('auto');
  const modalRef = useRef<HTMLDivElement>(null);
  
  // Trigger opening animation after mount
  useEffect(() => {
    requestAnimationFrame(() => setIsOpen(true));
  }, []);
  
  return {
    isOpen,
    setIsOpen,
    modalHeight,
    setModalHeight,
    modalWidth,
    setModalWidth,
    modalRef
  };
};

/**
 * Hook for managing progress dragging state
 */
export const useProgressDragging = () => {
  const [tempProgress, setTempProgress] = useState<number | null>(null);
  const [isDraggingProgress, setIsDraggingProgress] = useState(false);
  
  return {
    tempProgress,
    setTempProgress,
    isDraggingProgress,
    setIsDraggingProgress
  };
};

/**
 * Hook for syncing with project data updates
 */
export const useSyncWithProjectData = (
  editedTask: Partial<ProjectNode>,
  setEditedTask: Dispatch<SetStateAction<Partial<ProjectNode>>>,
  setCurrentChecklist: (checklist: ChecklistItem[]) => void,
  setTempProgress: (progress: number | null) => void,
  lastLocalUpdate: number,
  isDraggingProgress: boolean,
  hasChanges: boolean,
  projectData: { nodes?: ProjectNode[] } | null,
  pendingTitleAckRef?: React.MutableRefObject<{
    nodeId: string;
    title: string;
    committedAt: number;
  } | null>,
  owner?: PanelPersistenceState,
) => {
  const TITLE_ACK_TIMEOUT_MS = 10_000;
  const buildTaskSyncSignature = (task: Partial<ProjectNode>) => JSON.stringify({
    id: task.id,
    title: task.title,
    description: task.description,
    type: task.type,
    startDate: task.startDate,
    endDate: task.endDate,
    date: task.date,
    status: task.status,
    completionState: task.completionState,
    progress: task.progress,
    priority: task.priority,
    assignee: task.assignee,
    assignees: task.assignees || [],
    mentions: task.mentions || [],
    tags: task.tags || [],
    dependencies: task.dependencies || [],
    checklist: getNodeChecklist(task),
    workstream: task.workstream,
    developmentRoles: task.developmentRoles || [],
    visibilityTier: task.visibilityTier,
    classificationSource: task.classificationSource,
    classificationConfidence: task.classificationConfidence,
    category: task.category,
    updatedAt: task.updatedAt,
  });

  // Sync editedTask with projectData changes for existing tasks
  useEffect(() => {
    if (editedTask.id && projectData?.nodes) {
      const project = (node: Partial<ProjectNode>) => node.projectId || (node.id && getProjectIdFromNodeId(node.id, undefined));
      const updatedNode = projectData.nodes.find((node: ProjectNode) => getNodeId(node.id) === getNodeId(editedTask.id!) && project(node) === project(editedTask));
      if (updatedNode) {
        const timeSinceLastLocalUpdate = Date.now() - lastLocalUpdate;
        const authoritativeTask = withCanonicalChecklist(initializeNewTask(updatedNode));
        const pendingTitleAck = pendingTitleAckRef?.current;
        const hasPendingTitleAck = pendingTitleAck?.nodeId === editedTask.id;
        const authoritativeTitleAcknowledgesPending = hasPendingTitleAck
          && authoritativeTask.title === pendingTitleAck.title;
        const pendingTitleAckExpired = hasPendingTitleAck
          && Date.now() - pendingTitleAck.committedAt >= TITLE_ACK_TIMEOUT_MS;
        if (authoritativeTitleAcknowledgesPending || pendingTitleAckExpired) {
          // A matching authoritative title confirms the write. On timeout we
          // fail open to the server snapshot rather than pinning a potentially
          // rejected write indefinitely.
          pendingTitleAckRef!.current = null;
        }
        const retainPendingLocalTitle = hasPendingTitleAck
          && !authoritativeTitleAcknowledgesPending
          && !pendingTitleAckExpired;
        const authoritativeSnapshotChanged =
          buildTaskSyncSignature(authoritativeTask) !== buildTaskSyncSignature(editedTask);

        if (owner && !isDraggingProgress && authoritativeSnapshotChanged && timeSinceLastLocalUpdate > 250) {
          setEditedTask(previous => ({ ...previous, ...authoritativeTask, ...owner.fields, ...owner.activeFields,
            ...(panelChecklistPending(owner) ? { checklist: owner.checklist!.desired } : {}),
            ...(retainPendingLocalTitle && !('title' in owner.activeVersions) ? { title: pendingTitleAck!.title } : {}),
          }));
          setCurrentChecklist(panelChecklistPending(owner) ? owner.checklist!.desired : getNodeChecklist(authoritativeTask));
          return;
        }
        // When the panel is clean, replace it with the authoritative task snapshot so
        // remote MCP/admin mutations refresh all visible fields without waiting for reopen.
        if (!hasChanges && !isDraggingProgress && authoritativeSnapshotChanged && timeSinceLastLocalUpdate > 250) {
          setEditedTask((prevTask) => ({
            ...prevTask,
            ...authoritativeTask,
            // Do not overwrite the one locally acknowledged field with an
            // older parent snapshot. Other authoritative fields still refresh.
            title: retainPendingLocalTitle ? pendingTitleAck!.title : authoritativeTask.title,
          }));
          setCurrentChecklist(getNodeChecklist(authoritativeTask));
          // Reset temp progress if we're not dragging
          if (!isDraggingProgress) {
            setTempProgress(null);
          }
          return;
        }

      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectData?.nodes, editedTask.id, editedTask.progress, editedTask.updatedAt, lastLocalUpdate, isDraggingProgress, hasChanges, owner]);
};

/**
 * Hook for cleanup on unmount
 */
export type PendingProgress = { dragging: boolean; value: number | null; id: string | undefined; save: (id: string, progress: number) => Promise<void> | void; session: string | null; viewAs: string | null };
export const useProgressCleanup = (
  isDraggingProgress: boolean,
  tempProgress: number | null,
  editedTaskId: string | undefined,
  updateProgress: (id: string, progress: number) => Promise<void> | void,
  externalPending?: React.MutableRefObject<PendingProgress | null>,
) => {
  const localPending = useRef<PendingProgress | null>(null);
  const pending = externalPending || localPending;
  const sessionRef = useRef<{ active: boolean; cancelled: boolean; id?: string; session: string | null; viewAs: string | null; save: typeof updateProgress }>({ active: false, cancelled: false, session: null, viewAs: null, save: updateProgress });
  useLayoutEffect(() => {
    const previous = sessionRef.current;
    if (!isDraggingProgress) {
      sessionRef.current = { active: false, cancelled: false, session: null, viewAs: null, save: updateProgress };
      pending.current = null;
      return;
    }
    if (!previous.active) sessionRef.current = { active: true, cancelled: false, id: editedTaskId,
      session: getAuthRuntimeSessionIdentity(), viewAs: getAdminViewAsUserId(), save: updateProgress };
    const owner = sessionRef.current;
    if (owner.id !== editedTaskId || owner.session !== getAuthRuntimeSessionIdentity() || owner.viewAs !== getAdminViewAsUserId()) owner.cancelled = true;
    pending.current = owner.cancelled ? null : { dragging: true, value: tempProgress, id: editedTaskId, save: owner.save, session: owner.session, viewAs: owner.viewAs };
  }, [isDraggingProgress, tempProgress, editedTaskId, updateProgress, pending]);
  useEffect(() => () => {
    const current = pending.current;
    pending.current = null;
    if (current?.dragging && current.value !== null && current.id && current.session === getAuthRuntimeSessionIdentity() && current.viewAs === getAdminViewAsUserId()) {
      try { void Promise.resolve(current.save(current.id, current.value)).catch(() => undefined); } catch { /* The owning save handler presents failures. */ }
    }
  }, [pending]);
};
